import React, { useState, useEffect } from 'react';
import { ChevronDown, Eye } from 'lucide-react';
import AppointmentRow from './AppointmentRow';
import DocumentList from './DocumentList';
import appointmentService from '../../services/appointmentService';
import jobService from '../../services/jobService';
import { getFranchiseeIdForCRUD, getFranchiseeIdForDataLoading } from '../../App/services/appHelpers';

const formatTreatmentAddress = (job) => {
  const parts = [];
  if (job.treatment_address_line1) parts.push(job.treatment_address_line1);
  if (job.treatment_address_city) parts.push(job.treatment_address_city);
  if (job.treatment_address_postcode) parts.push(job.treatment_address_postcode);
  return parts.length > 0 ? parts.join(', ') : '';
};

const JobCard = ({ job, customer, user, onJobSelect, onViewInvoice, refreshKey }) => {
  const [expanded, setExpanded] = useState(false);
  const [appointments, setAppointments] = useState(null);
  const [documents, setDocuments] = useState(null);
  const [loading, setLoading] = useState(false);

  const address = formatTreatmentAddress(job);
  const totalAppointments = job.total_appointments || 0;

  // Load appointments + documents when expanded
  useEffect(() => {
    if (expanded && !appointments) {
      loadJobData();
    }
  }, [expanded]);

  // Reload documents when refreshKey changes (e.g. after creating invoice/report/estimate)
  useEffect(() => {
    if (expanded && appointments && refreshKey) {
      loadJobData();
    }
  }, [refreshKey]);

  const loadJobData = async () => {
    setLoading(true);
    try {
      const franchiseeId = getFranchiseeIdForDataLoading(user);

      // Load appointments and documents in parallel
      const [appointmentsRes, documentsRes] = await Promise.all([
        appointmentService.getAppointments(franchiseeId, { job_id: job.id }),
        jobService.getJobDocuments(franchiseeId, job.id)
      ]);

      const allAppointments = appointmentsRes.appointments || [];
      const now = new Date();

      // Sort: future first (ascending), then past (descending)
      const future = allAppointments
        .filter(a => new Date(a.scheduled_date) >= now)
        .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
      const past = allAppointments
        .filter(a => new Date(a.scheduled_date) < now)
        .sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date));

      setAppointments([...future, ...past]);
      setDocuments(documentsRes);
    } catch (error) {
      console.error('Error loading job data:', error);
      setAppointments([]);
      setDocuments({ invoices: [], treatmentReports: [], estimates: [], guarantees: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointment, newStatus) => {
    try {
      const franchiseeId = getFranchiseeIdForCRUD(user, appointment.franchisee_id);
      await appointmentService.updateAppointment(franchiseeId, appointment.id, { status: newStatus });
      // Update locally without full reload
      setAppointments(prev => prev.map(a => a.id === appointment.id ? { ...a, status: newStatus } : a));
    } catch (error) {
      console.error('Error updating appointment status:', error);
    }
  };

  const toggleExpand = (e) => {
    e.stopPropagation();
    setExpanded(!expanded);
  };

  // Job-level documents (linked to job but not a specific appointment)
  const jobLevelInvoices = (documents?.invoices || []).filter(i => i.job_id === job.id && !i.appointment_id);
  const jobLevelReports = (documents?.treatmentReports || []).filter(r => r.job_id === job.id && !r.appointment_id);
  const jobLevelEstimates = (documents?.estimates || []).filter(e => e.job_id === job.id && !e.appointment_id);
  const jobLevelGuarantees = (documents?.guarantees || []).filter(g => g.job_id === job.id && !g.appointment_id);

  const handleCardClick = () => {
    if (onJobSelect) onJobSelect(job);
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg overflow-visible hover:border-pest-blue/40 hover:shadow-sm transition-all cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Collapsed header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 truncate group-hover:text-pest-blue transition-colors">{job.category_name || 'Job'}</h4>
            {address && (
              <p className="text-sm text-gray-500 mt-0.5 truncate">{address}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {job.price && (
              <span className="font-semibold text-gray-900">
                £{parseFloat(job.price).toFixed(2)}
                {job.price_type && job.price_type !== 'total' && (
                  <span className="text-xs font-normal text-gray-500 ml-1">
                    /{job.price_type === 'per_visit' ? 'visit' : 'year'}
                  </span>
                )}
              </span>
            )}
            {job.estimated_visits && (
              <span className="text-xs text-gray-500">
                {job.estimated_visits} visits
              </span>
            )}
            <span className={`px-2 py-0.5 text-[11px] font-medium rounded-full uppercase ${
              job.status === 'active' ? 'bg-blue-100 text-blue-700' :
              job.status === 'on_hold' ? 'bg-yellow-100 text-yellow-700' :
              job.status === 'completed' ? 'bg-green-100 text-green-700' :
              job.status === 'cancelled' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {job.status?.replace('_', ' ')}
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); handleCardClick(); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 bg-pest-blue text-white text-xs font-medium rounded-lg hover:bg-pest-teal transition-colors"
            >
              <Eye size={13} /> View
            </button>
          </div>
        </div>

        {/* Expand toggle */}
        {totalAppointments > 0 && (
          <button
            onClick={toggleExpand}
            className="flex items-center gap-1 text-sm text-pest-blue hover:text-pest-teal mt-2 transition-colors"
          >
            <ChevronDown size={14} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
            <span>
              Appointments ({totalAppointments})
              {documents && (
                <>
                  {(documents.invoices?.length || 0) + (documents.treatmentReports?.length || 0) + (documents.estimates?.length || 0) + (documents.guarantees?.length || 0) > 0 && (
                    <span className="text-gray-400 ml-1">
                      &middot; {(documents.invoices?.length || 0) + (documents.treatmentReports?.length || 0) + (documents.estimates?.length || 0) + (documents.guarantees?.length || 0)} docs
                    </span>
                  )}
                </>
              )}
            </span>
          </button>
        )}

        {totalAppointments === 0 && (
          <p className="text-xs text-gray-400 mt-2">No appointments scheduled</p>
        )}
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-2" onClick={(e) => e.stopPropagation()}>
          {loading ? (
            <div className="py-4 text-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pest-blue mx-auto"></div>
              <p className="text-xs text-gray-400 mt-2">Loading...</p>
            </div>
          ) : (
            <>
              {/* Appointments */}
              {appointments && appointments.map(apt => (
                <AppointmentRow
                  key={apt.id}
                  appointment={apt}
                  documents={documents}
                  onViewInvoice={onViewInvoice}
                  onStatusChange={handleStatusChange}
                />
              ))}

              {/* Job-level documents (not linked to a specific appointment) */}
              {(jobLevelInvoices.length > 0 || jobLevelReports.length > 0 || jobLevelEstimates.length > 0 || jobLevelGuarantees.length > 0) && (
                <div className="pt-2 mt-2 border-t border-gray-100">
                  <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">Job-level documents</p>
                  <DocumentList
                    invoices={jobLevelInvoices}
                    treatmentReports={jobLevelReports}
                    estimates={jobLevelEstimates}
                    guarantees={jobLevelGuarantees}
                    onViewInvoice={onViewInvoice}
                  />
                </div>
              )}

            </>
          )}
        </div>
      )}
    </div>
  );
};

export default JobCard;
